/**
 * Simple Routing Configuration
 * Provides timeout configuration based on route complexity
 */

import type { Position } from '../types';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector';

export interface SimpleRoutingConfig {
  timeout: number;
  showProgress: boolean;
  enableRetry: boolean;
}

// Default timeout configurations
export const TIMEOUT_CONFIG = {
  SIMPLE_ROUTE: 15000,    // 15 seconds for short routes
  MEDIUM_ROUTE: 45000,    // 45 seconds for medium routes  
  COMPLEX_ROUTE: 90000,   // 90 seconds for long/complex routes
  MAX_TIMEOUT: 120000,    // Never exceed 2 minutes
};

// Distance thresholds (in kilometers)
export const DISTANCE_THRESHOLDS = {
  SHORT_ROUTE: 30,        // < 30km (local rides)
  MEDIUM_ROUTE: 100,      // 30-100km (day trips)
  LONG_ROUTE: 200,        // 100-200km (long day rides)
  VERY_LONG_ROUTE: 400,   // > 200km (multi-day distances)
};

// Waypoint count thresholds (still relevant for complexity)
export const WAYPOINT_THRESHOLDS = {
  SIMPLE: 3,              // <= 3 waypoints
  MEDIUM: 8,              // 4-8 waypoints
  COMPLEX: 15,            // 9+ waypoints
};

// User preferences (can be saved to localStorage)
export interface RoutingPreferences {
  maxTimeout: number;
  showProgressForLongRoutes: boolean;
  enableAutomaticRetry: boolean;
}

const DEFAULT_PREFERENCES: RoutingPreferences = {
  maxTimeout: 90000, // 90 seconds max to match complex routes
  showProgressForLongRoutes: true,
  enableAutomaticRetry: true
};

class SimpleRoutingConfigManager {
  private preferences: RoutingPreferences = DEFAULT_PREFERENCES;

  constructor() {
    this.loadPreferences();
  }

  /**
   * Get configuration for a specific route
   */
  getConfigForRoute(positions: Position[], _profile: RouteProfile): SimpleRoutingConfig {
    const waypointCount = positions.length;
    const estimatedDistance = this.calculateRouteDistance(positions);
    const estimatedComplexity = this.estimateComplexity(waypointCount, estimatedDistance);
    
    // Determine base timeout
    let timeout = TIMEOUT_CONFIG.SIMPLE_ROUTE;
    
    switch (estimatedComplexity) {
      case 'simple':
        timeout = TIMEOUT_CONFIG.SIMPLE_ROUTE;
        break;
      case 'medium':
        timeout = TIMEOUT_CONFIG.MEDIUM_ROUTE;
        break;
      case 'complex':
        timeout = TIMEOUT_CONFIG.COMPLEX_ROUTE;
        break;
    }

    // Apply user preference limits
    timeout = Math.min(timeout, this.preferences.maxTimeout);
    
    return {
      timeout,
      showProgress: estimatedComplexity !== 'simple' && this.preferences.showProgressForLongRoutes,
      enableRetry: this.preferences.enableAutomaticRetry
    };
  }

  /**
   * Update user preferences
   */
  updatePreferences(newPreferences: Partial<RoutingPreferences>): void {
    this.preferences = { ...this.preferences, ...newPreferences };
    this.savePreferences();
  }

  /**
   * Get current preferences
   */
  getPreferences(): RoutingPreferences {
    return { ...this.preferences };
  }

  /**
   * Estimate route complexity based on waypoint count and distance
   */
  private estimateComplexity(waypointCount: number, estimatedDistanceKm: number): 'simple' | 'medium' | 'complex' {
    // Distance-based complexity (primary factor)
    let distanceComplexity: 'simple' | 'medium' | 'complex' = 'simple';
    if (estimatedDistanceKm > DISTANCE_THRESHOLDS.LONG_ROUTE) {
      distanceComplexity = 'complex';
    } else if (estimatedDistanceKm > DISTANCE_THRESHOLDS.MEDIUM_ROUTE) {
      distanceComplexity = 'medium';
    } else if (estimatedDistanceKm > DISTANCE_THRESHOLDS.SHORT_ROUTE) {
      distanceComplexity = 'medium';
    }

    // Waypoint-based complexity (secondary factor)
    let waypointComplexity: 'simple' | 'medium' | 'complex' = 'simple';
    if (waypointCount > WAYPOINT_THRESHOLDS.COMPLEX) {
      waypointComplexity = 'complex';
    } else if (waypointCount > WAYPOINT_THRESHOLDS.MEDIUM) {
      waypointComplexity = 'medium';
    } else if (waypointCount > WAYPOINT_THRESHOLDS.SIMPLE) {
      waypointComplexity = 'medium';
    }

    // Use the higher complexity of the two factors
    const complexityLevels = { 'simple': 0, 'medium': 1, 'complex': 2 };
    const maxComplexity = Math.max(complexityLevels[distanceComplexity], complexityLevels[waypointComplexity]);
    
    return Object.keys(complexityLevels)[maxComplexity] as 'simple' | 'medium' | 'complex';
  }

  /**
   * Calculate estimated route distance using haversine formula between waypoints
   */
  private calculateRouteDistance(positions: Position[]): number {
    if (positions.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      totalDistance += this.haversineDistance(positions[i], positions[i + 1]);
    }

    return totalDistance;
  }

  /**
   * Calculate haversine distance between two points in kilometers
   */
  private haversineDistance(pos1: Position, pos2: Position): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad(pos2.lat - pos1.lat);
    const dLng = this.toRad(pos2.lng - pos1.lng);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(pos1.lat)) * Math.cos(this.toRad(pos2.lat)) * 
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(): void {
    try {
      localStorage.setItem('cyclingbuddy_routing_config', JSON.stringify(this.preferences));
    } catch (error) {
      console.warn('Failed to save routing preferences:', error);
    }
  }

  /**
   * Load preferences from localStorage
   */
  private loadPreferences(): void {
    try {
      const saved = localStorage.getItem('cyclingbuddy_routing_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.preferences = { ...DEFAULT_PREFERENCES, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load routing preferences:', error);
      this.preferences = DEFAULT_PREFERENCES;
    }
  }

  /**
   * Reset to default preferences
   */
  resetToDefaults(): void {
    this.preferences = { ...DEFAULT_PREFERENCES };
    this.savePreferences();
  }
}

// Singleton instance
export const routingConfig = new SimpleRoutingConfigManager();

// Utility functions for direct use
export const getTimeoutForRoute = (positions: Position[], profile: RouteProfile): number => {
  return routingConfig.getConfigForRoute(positions, profile).timeout;
};

export const shouldShowProgressForRoute = (positions: Position[], profile: RouteProfile): boolean => {
  return routingConfig.getConfigForRoute(positions, profile).showProgress;
};
